import ModalCover from 'components/shared/modal/ModalCover';
import styles from './AuthorizationGuard.module.css';
import { ActivatePluginButton, useIsPluginActive } from 'features/plugins';
import { useState, useEffect, useContext } from 'react';
import ModalInputField from 'components/shared/modal/ModalInputField';
import { testPrivateKey } from 'utils/validations';
import { useModalSystem } from 'config/context/ModalSystemContext';
import { useSetHotKey } from 'features/permissions/services/useSetHotKey';
import ActionProgress from 'components/shared/status/ActionProgress';
import ActionProgressSuccess from 'components/shared/status/ActionProgressSuccess';
import ActionProgressFailed from 'components/shared/status/ActionProgressFailed';
import ThemeContext from 'config/context/themeContext';

const AuthorizationGuard = ({ title = "Authorization Guard", permission, setTempPrivateKey }) => {
    const { dark } = useContext(ThemeContext);
    const { hideModal } = useModalSystem();
    const [pk, setPk] = useState('');
    const [error, setError] = useState('');
    const [tempKeySuccess, setTempKeySuccess] = useState(false);
    const isPluginActive = useIsPluginActive(7);
    const [localPluginActive, setLocalPluginActive] = useState(isPluginActive);
    const { loading, response, setHotKey, resetServerStatus } = useSetHotKey();

    useEffect(() => {
        if (response || tempKeySuccess) {
            const timer = setTimeout(() => {
                if (response) {
                    resetServerStatus();
                    if (response.status) {
                        hideModal();
                    }
                }
                if (tempKeySuccess) {
                    setTempKeySuccess(false);
                    hideModal();
                }
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [response, tempKeySuccess, resetServerStatus, hideModal]);

    const storeKey = () => {
        if (!testPrivateKey(pk)) return setError('Invalid private key!');
        setError('');
        if (isPluginActive) {
            setHotKey({ permission, pk });
        } else if (setTempPrivateKey) {
            setTempPrivateKey(pk);
            setTempKeySuccess(true);
        }
    }

    return (
        <ModalCover size='md' closeOnOverlay={true}>
            <div className={`${styles.authorization_guard} ${dark ? styles.dark : ''}`}>
                <h3>{title}</h3>
                <p>To enable data manipulation, a private key must be provided. Please choose one of the following options.</p>
                <div className={styles.key_block}>
                    <div className={styles.hot_key}>
                        {!localPluginActive ? (
                            <>
                                <span className={styles.hot_key_info_black}>Hot key is not activated.</span>
                                <div className={styles.activate_plugin_btn}>
                                    <ActivatePluginButton id="7" onSuccess={() => setLocalPluginActive(true)}>
                                        Activate plugin
                                    </ActivatePluginButton>
                                </div>
                            </>
                        ) : (
                            <span className={styles.hot_key_info_blue}>Hot Key plugin is activated.</span>
                        )}
                    </div>
                    <div className={styles.insert_key}>
                        <ModalInputField
                            privateKey
                            placeholder="Private key"
                            value={pk}
                            setValue={setPk}
                            valid={!error}
                        />
                    </div>
                    {error && <span className={styles.error_msg}>{error}</span>}
                    <div className={styles.actions}>
                        <button onClick={hideModal}>Continue without key</button>
                        <button onClick={storeKey}>Apply key</button>
                    </div>
                </div>
                {loading && <ActionProgress stepInterval={500} />}
                {response?.status && <ActionProgressSuccess message={response?.message} onClose={resetServerStatus} />}
                {response && !response?.status && <ActionProgressFailed message={response?.message} onClose={resetServerStatus} />}
                {tempKeySuccess && <ActionProgressSuccess message="Temporary private key set successfully!" onClose={() => setTempKeySuccess(false)} />}
            </div>
        </ModalCover>
    )
}

export default AuthorizationGuard;